/**
 * @fileoverview Who teaches this class — and who used to.
 *
 * A class was staffed once, at creation, and never again: the only way to
 * change an instructor was to catch the class before anyone was named, and the
 * only record of a handover was that the name had silently changed. Two things
 * follow from that, and this panel is both of them.
 *
 * **A class can be taught by two people.** Co-teaching is ordinary — a lead and
 * an assistant, two instructors splitting a large ensemble, a replacement
 * shadowing the person they are taking over from. Every active instructor sees
 * the class in their own app, its meetings on their own calendar, and may write
 * the lesson up; whoever writes it is credited with it.
 *
 * **Leaving is recorded, not erased.** An instructor who steps back goes
 * INACTIVE rather than disappearing: the class stops appearing in their app and
 * on their calendar, and what they already taught stays theirs, still readable
 * beside the person who took over. That is the honest shape of a handover
 * mid-term, and it is why the row stays on the page afterwards.
 *
 * Make inactive asks first (iOS review C8), naming what changes, because the
 * class leaves that person's app and calendar at once; Bring back acts at once.
 *
 * A class still linked to a legacy Scheduler offering keeps that offering's
 * single instructor binding. Migrated block series support co-teaching.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Avatar, Button, Card, Icon, ListRow, SectionHeader, Tag } from "@/components/studio/primitives";
import AddClassInstructorDialog from "@/components/org/AddClassInstructorDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** "Sep 18" from an ISO instant or a YYYY-MM-DD, without tripping over UTC. */
function shortDay(value) {
  const ymd = String(value || "").slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * The line under a name. An active instructor's is when they took the class
 * on; an inactive one's is when they stepped back — the two facts an
 * administrator is actually reconstructing when they open this months later.
 */
function historyLine(row) {
  const lessons = row.lessons_taught || 0;
  const taught = lessons ? `${lessons} lesson${lessons === 1 ? "" : "s"} written` : null;
  if (row.status === "inactive") {
    return [row.ended_at ? `Stepped back ${shortDay(row.ended_at)}` : "No longer teaching this class", taught]
      .filter(Boolean)
      .join(" · ");
  }
  return [row.added_at ? `Teaching since ${shortDay(row.added_at)}` : "Teaching this class", taught]
    .filter(Boolean)
    .join(" · ");
}

export default function ClassInstructorsCard({ cls, instructors = [], programInstructors = [], onChanged }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState("");
  // The instructor a Make inactive is asking about, until it is answered.
  const [confirming, setConfirming] = useState(null);

  const active = useMemo(() => instructors.filter((i) => i.status !== "inactive"), [instructors]);
  const inactive = useMemo(() => instructors.filter((i) => i.status === "inactive"), [instructors]);
  const connected = Boolean(cls.scheduler_offering_id);

  const setStatus = async (row, status) => {
    setBusy(row.user_id);
    try {
      await orgApi.put(`/org/classes/${cls.class_id}/instructors/${row.user_id}`, { status });
      toast.success(
        status === "inactive"
          ? `${row.name} no longer teaches ${cls.name} — what they taught stays theirs`
          : `${row.name} is teaching ${cls.name} again`,
      );
      onChanged?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't change that instructor"));
    } finally {
      setBusy("");
    }
  };

  /* Ordered active-then-inactive rather than by date: the question the page is
     asked is "who teaches this", and the history is the answer underneath it. */
  const rows = [...active, ...inactive];

  return (
    <>
      <SectionHeader
        action={connected ? undefined : "+ Add instructor"}
        onAction={() => setAdding(true)}
        actionTestId="open-add-class-instructor"
      >
        Instructors ({active.length})
      </SectionHeader>
      <AlertDialog open={Boolean(confirming)} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent className="mx-4 max-w-[calc(100%-2rem)] border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Make {confirming?.name} inactive?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              {confirming?.name} stops teaching {cls.name} from today: it leaves their app and calendar, and no notes
              are owed from them. What they taught stays theirs. Bring back undoes it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const row = confirming;
                setConfirming(null);
                setStatus(row, "inactive");
              }}
              className="bg-[var(--state-danger)] text-[var(--text-on-state)]"
              data-testid="class-instructor-inactive-confirm"
            >
              Make inactive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card padding={0} className="mb-6" data-testid="class-instructors">
        {rows.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">
            Nobody teaches this class yet. Assign an instructor before reserving their calendar or writing a lesson.
          </div>
        ) : (
          rows.map((row, i) => (
            <ListRow
              key={row.user_id}
              borderBottom={i < rows.length - 1}
              data-testid={`class-instructor-${row.user_id}`}
              leading={<Avatar name={row.name} size={36} accent={row.status !== "inactive"} />}
              title={row.name}
              subtitle={historyLine(row)}
              /* Deliberately NOT dimmed with opacity. A former instructor's row
                 is a record somebody reads months later to work out who taught
                 which half of the term, and a wash over it puts its text under
                 the contrast floor in both themes — where the token layer can
                 no longer answer for it. The tag and the avatar carry the
                 state instead. */
              trailing={
                <span className="flex shrink-0 items-center gap-2">
                  {row.status === "inactive" ? <Tag>Inactive</Tag> : <Tag accent>Active</Tag>}
                  {!connected && (
                    <Button
                      size="sm"
                      variant="bare"
                      disabled={busy === row.user_id}
                      data-testid={`class-instructor-toggle-${row.user_id}`}
                      onPress={() => (row.status === "inactive" ? setStatus(row, "active") : setConfirming(row))}
                    >
                      {row.status === "inactive" ? "Bring back" : "Make inactive"}
                    </Button>
                  )}
                </span>
              }
            />
          ))
        )}
        {connected && (
          <div className="flex items-start gap-2 border-t border-[var(--studio-divider)] px-[18px] py-[14px] font-sans text-[13px] text-[var(--text-tertiary)]">
            <Icon name="link" size={15} className="mt-[2px] shrink-0" />
            <span>
              This class is scheduled in Kalver Scheduler, which holds its instructor. Change it there, or make a
              separate class in Hans for a second instructor.
            </span>
          </div>
        )}
      </Card>

      <AddClassInstructorDialog
        open={adding}
        onOpenChange={setAdding}
        cls={cls}
        current={instructors}
        programInstructors={programInstructors}
        onAdded={onChanged}
      />
    </>
  );
}
